import {
    useState,
    useMemo, useEffect,
} from 'react'
import useContractBasisCreateQuoteReducer
    from 'app/PoC/DataGridDemoPages/ContractBasisDemo/ContractBasisDemoCreateQuote/useContractBasisCreateQuoteReducer'
import FinalSelectionStep
    from 'app/PoC/DataGridDemoPages/ContractBasisDemo/ContractBasisDemoCreateQuote/steps/FinalSelectionStep/FinalSelectionStep'

import HistoryBlockPrompt from 'app/shared-components/HistoryBlockPrompt'
import useBeforeUnloadDialog from 'app/hooks/useBeforeUnloadDialog'
import SkyNetStepper from 'app/shared-components/SkyNetStepper'
import SelectAddFeesLaneGroupStep
    from 'app/PoC/DataGridDemoPages/ContractBasisDemo/ContractBasisDemoCreateQuote/steps/SelectAddFeesLaneGroupStep/SelectAddFeesLaneGroupStep'
import FilterLaneAndFees
    from './steps/FilterLaneAndFees/FilterLaneAndFees'
import {
    ContractBasisCreateQuoteContext,
} from './ContractBasisCreateQuoteContext'

import useStyles from './ContractBasisDemoCreateQuote.style'

export default function ContractBasisDemoCreateQuote({
    id,
    quoteId = undefined,
    showContactsSelections = true,
    renderActions,
}: Readonly<{
    id: number,
    quoteId?: number,
    showContactsSelections?: boolean,
    renderActions: (params: any) => JSX.Element,
}>) {
    const {
        classes,
    } = useStyles()

    const {
        state,
        setAdditionalFee,
        setLaneGroups,
        setLaneFeesSelectedRows,
        setAdditionFeesSelectedRows,
        setFiltersState,
        setPreviewData,
        setContactsSelection,
    } = useContractBasisCreateQuoteReducer()

    const [
        activeStep,
        setActiveStep,
    ] = useState<number>(0)

    const [
        isEdited,
        setIsEdited,
    ] = useState<boolean>()

    useEffect(() => {
        if (state?.additionalFee || state?.laneGroups?.length) {
            setIsEdited(true)
        }
    }, [state])

    useBeforeUnloadDialog(isEdited)

    const stepperSteps = useMemo(() => {
        return [
            {
                key: '1',
                label: 'Select Relevant Additional Fees And Lane Groups',
                expanded: true,
                content: <SelectAddFeesLaneGroupStep
                    state={state}
                    setAdditionalFee={setAdditionalFee}
                    setLaneGroups={setLaneGroups}
                    classes={classes}
                />,
            },
            {
                key: '2',
                label: 'Filter Lanes and Lane Fees',
                expanded: (step: number) => {
                    return step > 0
                },
                content: <FilterLaneAndFees
                    state={state}
                    classes={classes}
                    showContactsSelections={showContactsSelections}
                    setLaneFeesSelectedRows={setLaneFeesSelectedRows}
                    setAdditionFeesSelectedRows={setAdditionFeesSelectedRows}
                    setFiltersState={setFiltersState}
                    setPreviewData={setPreviewData}
                    setContactsSelection={setContactsSelection}
                />,
            },
            {
                key: '3',
                label: 'Confirm Final Selection',
                expanded: false,
                content: <FinalSelectionStep
                    state={state}
                    classes={classes}
                    renderActions={renderActions}
                />,
            },
        ]
    }, [
        classes,
        renderActions,
        setAdditionFeesSelectedRows,
        setAdditionalFee,
        setContactsSelection,
        setFiltersState,
        setLaneFeesSelectedRows,
        setLaneGroups,
        setPreviewData,
        showContactsSelections,
        state,
    ])

    const context = useMemo(() => {
        return {
            activeStep,
            setActiveStep,
            contractBasisId: id,
            quoteId,
            setIsEdited,
        }
    }, [
        activeStep,
        id,
        quoteId,
    ])

    return (
        <ContractBasisCreateQuoteContext.Provider value={context}>
            <SkyNetStepper
                activeStep={activeStep}
                steps={stepperSteps}
            />
            <HistoryBlockPrompt
                when={isEdited}
                positiveLabel="Discard"
                isPositiveAlert
                promptText="You are about to leave this page, your progress will be lost. Would you like to discard your changes anyway?"
            />
        </ContractBasisCreateQuoteContext.Provider>
    )
}
